import React, { useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { handleApiRequest } from '../../../utils/apiHandler';
import api from '../../../services/axios';

const ItemSection = ({ items, setItems, routes, itemDetailsRefs }) => {
  const searchTimeoutRef = useRef(null);
  const [itemSuggestions, setItemSuggestions] = useState([]);
  const [activeItemId, setActiveItemId] = useState(null);
  const [photoFetchLoadingItemId, setPhotoFetchLoadingItemId] = useState(null);

  const handleItemNameChange = (itemId, search_name) => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === itemId ? { ...item, item_name: search_name } : item
      )
    );

    setActiveItemId(itemId);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(async () => {
      if (search_name.length < 1) {
        setItemSuggestions([]);
        return;
      }

      await handleApiRequest({
        requestFn: () =>
          api.get(`/manager/pickups/items/suggestions`, {
            params: { query: search_name },
          }),
        onSuccess: (res) => {
          setItemSuggestions(res);
        },
        onError: (error) => {
          console.error('Error fetching item suggestions', error);
          setItemSuggestions([]);
        },
      });
    }, 300);
  };

  const handleSuggestionSelect = async (itemId, suggestion) => {
    setPhotoFetchLoadingItemId(itemId);

    setItems((prevItems) =>
      prevItems.map((item) => {
        if (item.id === itemId) {
          return {
            ...item,
            item_name: suggestion.item_name,
            weight: suggestion.weight || item.weight,
            details: suggestion.details || item.details,
            size: suggestion.size || item.size,
            photo: null,
            photoUrl: suggestion.photo,
          };
        }
        return item;
      })
    );

    setItemSuggestions([]);
    setActiveItemId(null);
    setPhotoFetchLoadingItemId(null);
  };
  const handleRemoveItem = (id) => {
    setItems((prevItems) => prevItems.filter((item) => item.id !== id));
    delete itemDetailsRefs.current[id];
  };

  const handleItemChange = (id, field, value) => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  const handleItemRouteChange = (id, newRouteId) => {
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.id === id ? { ...item, routeId: newRouteId } : item
      )
    );
  };

  const handleFileChange = (id, file) => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      setItems((prevItems) =>
        prevItems.map((item) =>
          item.id === id
            ? {
                ...item,
                photo: file,
                photoUrl: e.target.result, // base64 data URL
              }
            : item
        )
      );
    };
    reader.readAsDataURL(file);
  };

  const adjustItemTextareaHeight = (itemId) => {
    const textarea = itemDetailsRefs.current[itemId];
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  useEffect(() => {
    items.forEach((item) => {
      adjustItemTextareaHeight(item.id);
    });
  }, [items]);
  return (
    <>
      <table className="table table-sm table-borderless">
        <tbody>
          {items &&
            items.map((item) => {
              const routeOptions = routes.map((route) => ({
                value: route.id,
                label: `Route ${route.id}`,
              }));

              return (
                <React.Fragment key={item.id}>
                  <tr style={{ position: 'relative' }}>
                    <td>
                      <input
                        type="text"
                        value={item.item_name}
                        onChange={(e) =>
                          handleItemNameChange(item.id, e.target.value)
                        }
                        onFocus={() => setActiveItemId(item.id)}
                        onBlur={() => {
                          setTimeout(() => {
                            setItemSuggestions([]);
                            setActiveItemId(null);
                          }, 200);
                        }}
                        placeholder="Item Name"
                        className="form-control"
                      />

                      {/* Only show if this item is active AND we have suggestions */}
                      {activeItemId === item.id &&
                        itemSuggestions.length > 0 && (
                          <ul
                            className="list-group"
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              zIndex: 999,
                              maxHeight: '300px',
                              overflowY: 'auto',
                              background: '#fff',
                              border: '1px solid #d4d4d4',
                            }}
                          >
                            {itemSuggestions.map((sugg) => (
                              <li
                                key={sugg.item_name}
                                className="list-group-item list-group-item-action"
                                style={{ cursor: 'pointer' }}
                                onMouseDown={() =>
                                  handleSuggestionSelect(item.id, sugg)
                                }
                              >
                                {sugg.item_name} | {sugg.weight} | {sugg.size}
                              </li>
                            ))}
                          </ul>
                        )}
                    </td>
                    <td>
                      <input
                        type="text"
                        value={item.weight}
                        onChange={(e) =>
                          handleItemChange(item.id, 'weight', e.target.value)
                        }
                        placeholder="Weight"
                        className="form-control"
                      />
                    </td>
                    <td>
                      <select
                        value={item.size}
                        onChange={(e) =>
                          handleItemChange(item.id, 'size', e.target.value)
                        }
                        className="custom-select"
                      >
                        <option value="">Select Size</option>
                        <option value="Small">Small</option>
                        <option value="Medium">Medium</option>
                        <option value="Large">Large</option>
                        <option value="Extra Large">Extra Large</option>
                      </select>
                    </td>
                    <td>
                      <select
                        value={item.routeId}
                        onChange={(e) =>
                          handleItemRouteChange(
                            item.id,
                            parseInt(e.target.value)
                          )
                        }
                        className="custom-select"
                        style={{ minWidth: '6rem' }}
                      >
                        {routeOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td rowSpan="2">
                      {items.length > 1 && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger mt-1"
                          onClick={() => handleRemoveItem(item.id)}
                        >
                          <i className="fa fa-trash" />
                        </button>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="4">
                      <textarea
                        ref={(el) => {
                          itemDetailsRefs.current[item.id] = el;
                        }}
                        value={item.details}
                        onChange={(e) => {
                          handleItemChange(item.id, 'details', e.target.value);
                          adjustItemTextareaHeight(item.id);
                        }}
                        placeholder="Eg. 4' x 5' x 5' fragile"
                        className="form-control"
                        rows={1}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="4">
                      <input
                        className="form-control"
                        type="file"
                        id={`formFile-${item.id}`}
                        onChange={(e) =>
                          handleFileChange(item.id, e.target.files[0])
                        }
                      />

                      {(photoFetchLoadingItemId === item.id ||
                        item.photoUrl) && (
                        <div
                          style={{
                            position: 'relative',
                            width: '100px',
                            height: '100px',
                          }}
                        >
                          {photoFetchLoadingItemId === item.id && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                              }}
                            >
                              <Spinner
                                animation="border"
                                className="text-primary"
                              />
                            </div>
                          )}

                          {/* Image preview */}
                          {item.photoUrl && (
                            <div className="mt-2">
                              <img
                                src={item.photoUrl}
                                alt="Preview"
                                style={{
                                  maxWidth: '100px',
                                  maxHeight: '100px',
                                  objectFit: 'cover',
                                }}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td colSpan="5">
                      <hr className="my-0" />
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
        </tbody>
      </table>
    </>
  );
};

export default ItemSection;
